import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { trackingReportApi } from '../lib/api';

interface Props {
  onClose: () => void;
  onCreated: () => void;
  editors: { id: string; name: string; display_name: string }[];
  canSelectEditor: boolean;
  defaultDate?: string;
}

export function ManualEntryModal({ onClose, onCreated, editors, canSelectEditor, defaultDate }: Props) {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [editorId, setEditorId] = useState('');
  const [date, setDate] = useState(defaultDate || '');
  const [videosCompleted, setVideosCompleted] = useState('');
  const [videosAssigned, setVideosAssigned] = useState('');
  const [vertical, setVertical] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    const cc = parseInt(videosCompleted) || 0;
    const ac = parseInt(videosAssigned) || cc; // default assigned = completed

    setSaving(true);
    setError('');
    try {
      await trackingReportApi.createManualEntry({
        title: title.trim(),
        note: note.trim() || undefined,
        editor_id: editorId || undefined,
        date: date || undefined,
        completed_creatives: cc || undefined,
        assigned_creatives: ac || undefined,
        vertical: vertical.trim() || undefined,
      } as any);
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Manual Entry</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
              {error}
            </div>
          )}

          {canSelectEditor && editors.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Editor</label>
              <select
                value={editorId}
                onChange={e => setEditorId(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Auto-detect (my editor)</option>
                {editors.map(ed => (
                  <option key={ed.id} value={ed.id}>{ed.display_name || ed.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date (optional)</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank to use today's date</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Internal edits, Team meeting, Script planning"
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vertical (optional)</label>
            <input
              type="text"
              value={vertical}
              onChange={e => setVertical(e.target.value)}
              placeholder="e.g., Home Insurance, Medicare"
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Videos Completed</label>
              <input
                type="number"
                min="0"
                value={videosCompleted}
                onChange={e => setVideosCompleted(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Videos Assigned</label>
              <input
                type="number"
                min="0"
                value={videosAssigned}
                onChange={e => setVideosAssigned(e.target.value)}
                placeholder="Same as completed"
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400"
              />
              <p className="text-xs text-gray-400 mt-0.5">Leave blank = same as completed</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              placeholder="Add details about this task..."
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              <Plus size={14} />
              {saving ? 'Creating...' : 'Create Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
